import Phaser from 'phaser';
import { themes, deskStyles } from './themes';
import {
  mulberry32,
  generateDecorMulti,
  generateGroundPatches,
  pickRandomSpawn,
  pointInRect,
  type ExcludeRect
} from './world';
import { ensureDecorTextures } from './decor';
import {
  ensureWorkTextures,
  pickRandomSpec,
  specsForLevel,
  STAGE_TEXTURES,
  workSpecsByType,
  type WorkSpec
} from './work-items';
import {
  careerLevels,
  levelMeta,
  nextLevel,
  maxLevel,
  thresholdFor,
  type CareerLevel
} from './levels';
import { getFlags, onFlagsChange } from '../flags/launchdarkly';
import { getTouch } from '../controls';
import {
  showStartOverlay,
  hideStartOverlay,
  onStartButton,
  onTutorialSkip,
  showCompliancePopup,
  hideCompliancePopup,
  showTutorialBanner,
  hideTutorialBanner
} from '../ui';
import { hudEl } from '../hud';
import { isUpdatePending, reloadForUpdate } from '../version-watcher';
import type { FlagSet } from '../flags/types';
import {
  TUTORIAL_ORDER,
  TUTORIAL_STEPS,
  tutorialCompleted,
  markTutorialCompleted,
  type TutorialStep
} from '../tutorial';

// Desktop world dimensions. On small screens we shrink the world (matched to
// viewport aspect) so the three desks fit in roughly one camera view —
// computed per-scene in computeWorldSize().
const DESKTOP_WORLD_WIDTH = 1600;
const DESKTOP_WORLD_HEIGHT = 1000;
const SMALL_SCREEN_THRESHOLD = 800;
const DESK_W = 192;
const DESK_H = 144;
const PLAYER_BASE_SPEED = 160;
const PLAYER_FAST_SPEED = 240;
const CARRY_CAP_BASE = 5;
const CARRY_CAP_BIG = 12;
const SPAWN_INTERVAL_MS = 1300;
const MAX_ACTIVE_ITEMS = 20;
const REJECTION_RATE = 0.2;
const LEVEL_DURATION_MS = 60_000;
const DESK_COOLDOWN_MS = 600;
const COMPLIANCE_PAUSE_MS = 2000;

type SceneState = 'pregame' | 'playing' | 'gameover';

type EntryStage = 'work' | 'tbe' | 'approved' | 'rejected';

interface Entry {
  id: number;
  workType: string;
  stage: EntryStage;
  value: number;
  sprite: Phaser.GameObjects.Sprite;
}

type DeskKey = 'office' | 'compliance' | 'cash';

interface Desk {
  key: DeskKey;
  rect: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  zone: Phaser.GameObjects.Zone;
  cx: number;
  cy: number;
  topY: number;
  bounds: ExcludeRect;
  cooldownUntil: number;
  glow: Phaser.GameObjects.Rectangle;
  glowTween?: Phaser.Tweens.Tween;
}

type WorkSprite = Phaser.Physics.Arcade.Sprite & {
  __spec?: WorkSpec;
  __expiresAt?: number;
  __bar?: Phaser.GameObjects.Rectangle;
  __valueLabel?: Phaser.GameObjects.Text;
};

type FixSprite = Phaser.Physics.Arcade.Sprite & {
  __entryId?: number;
};

interface AvatarStyle {
  hair: number;
  skin: number;
  eyes: number;
  pants: number;
  shirt: number;
  shirtAlt?: number;
  tie?: number;
  tiePin?: number;
  vest?: number;
  glasses?: number;
  earpiece?: number;
  earpieceWire?: number;
  badge?: number;
  pocketSquare?: number;
  lapel?: number;
  collarV?: boolean;
  hairTuft?: 'side' | 'long';
}

const AVATAR_STYLES: Record<CareerLevel, AvatarStyle> = {
  admin: {
    hair: 0x4b2c20,
    skin: 0xf5d3a3,
    eyes: 0x111111,
    pants: 0x1f2937,
    shirt: 0xfb923c,
    shirtAlt: 0xfde68a,
    earpiece: 0x111111,
    earpieceWire: 0x4b5563,
    badge: 0x9a3412
  },
  paralegal: {
    hair: 0x4b2c20,
    skin: 0xf5d3a3,
    eyes: 0x111111,
    pants: 0x312e81,
    shirt: 0xe5e7eb,
    shirtAlt: 0xffffff,
    tie: 0x4f46e5,
    glasses: 0x111111,
    badge: 0x4338ca,
    hairTuft: 'long'
  },
  attorney: {
    hair: 0x2d1f17,
    skin: 0xf5d3a3,
    eyes: 0x111111,
    pants: 0x111827,
    shirt: 0x1f2937,
    shirtAlt: 0xffffff,
    tie: 0x7c3aed,
    lapel: 0x0b1220,
    collarV: true
  },
  partner: {
    hair: 0xc7c5be,
    skin: 0xf5d3a3,
    eyes: 0x111111,
    pants: 0x000000,
    shirt: 0x0b1220,
    shirtAlt: 0xffffff,
    vest: 0x000000,
    tie: 0xfbbf24,
    tiePin: 0xfde047,
    pocketSquare: 0xfbbf24,
    lapel: 0x000000,
    collarV: true,
    hairTuft: 'side'
  }
};

export class GameScene extends Phaser.Scene {
  private player!: Phaser.Physics.Arcade.Sprite;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasdKeys!: Record<'W' | 'A' | 'S' | 'D', Phaser.Input.Keyboard.Key>;
  private arrowState = { up: false, down: false, left: false, right: false };
  private workItems!: Phaser.Physics.Arcade.Group;
  private fixItems!: Phaser.Physics.Arcade.Group;
  private decorContainer!: Phaser.GameObjects.Container;
  private groundPatchContainer!: Phaser.GameObjects.Container;
  private groundLayer!: Phaser.GameObjects.Rectangle;
  private officeDesk!: Desk;
  private complianceDesk!: Desk;
  private cashDesk!: Desk;
  private idleMessage!: Phaser.GameObjects.Text;
  private carrying: Entry[] = [];
  private cash = 0;
  private flags: FlagSet = getFlags();
  private prevFlags: FlagSet | null = null;
  private nextEntryId = 1;
  private levelEndsAt = 0;
  private state: SceneState = 'pregame';
  private currentLevel: CareerLevel = 'admin';
  private fixIconDataUrl = '';
  private tutorialActive = false;
  private tutorialStep: TutorialStep | null = null;
  private worldWidth = DESKTOP_WORLD_WIDTH;
  private worldHeight = DESKTOP_WORLD_HEIGHT;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    this.flags = getFlags();
    const size = this.computeWorldSize();
    this.worldWidth = size.w;
    this.worldHeight = size.h;
    this.makePlayerTextures();
    this.createPlayerAnimations();
    ensureWorkTextures(this);
    this.fixIconDataUrl = this.extractTextureAsDataUrl(STAGE_TEXTURES.fix);
    for (const theme of Object.values(themes)) {
      ensureDecorTextures(this, theme.decorVariants);
    }

    this.physics.world.setBounds(0, 0, this.worldWidth, this.worldHeight);
    this.cameras.main.setBounds(0, 0, this.worldWidth, this.worldHeight);

    this.groundLayer = this.add
      .rectangle(0, 0, this.worldWidth, this.worldHeight, 0x000000)
      .setOrigin(0, 0)
      .setDepth(-100);
    this.groundPatchContainer = this.add.container(0, 0).setDepth(-90);
    this.decorContainer = this.add.container(0, 0).setDepth(-50);

    this.officeDesk = this.createDesk('office', 64, this.worldHeight - DESK_H - 64, deskStyles.office);
    this.complianceDesk = this.createDesk(
      'compliance',
      (this.worldWidth - DESK_W) / 2,
      64,
      deskStyles.compliance
    );
    this.cashDesk = this.createDesk(
      'cash',
      this.worldWidth - DESK_W - 64,
      this.worldHeight - DESK_H - 64,
      deskStyles.cash
    );

    this.workItems = this.physics.add.group();
    this.fixItems = this.physics.add.group();

    this.player = this.physics.add.sprite(
      this.worldWidth / 2,
      this.worldHeight / 2,
      this.playerIdleKey()
    );
    this.player.setCollideWorldBounds(true);
    this.player.body!.setSize(20, 22).setOffset(2, 10);
    this.player.setDepth(10);

    this.physics.add.overlap(this.player, this.workItems, (_p, item) =>
      this.collectWorkItem(item as WorkSprite)
    );
    this.physics.add.overlap(this.player, this.fixItems, (_p, fix) =>
      this.collectFixItem(fix as FixSprite)
    );
    this.physics.add.overlap(this.player, this.officeDesk.zone, () => this.handleOffice());
    this.physics.add.overlap(this.player, this.complianceDesk.zone, () =>
      this.handleCompliance()
    );
    this.physics.add.overlap(this.player, this.cashDesk.zone, () => this.handleCash());

    this.cameras.main.startFollow(this.player, true, 0.12, 0.12);
    this.updateCameraZoom();

    this.idleMessage = this.add
      .text(0, 0, '', {
        fontFamily: 'ui-monospace, Menlo, monospace',
        fontSize: '18px',
        color: '#ffffff',
        backgroundColor: 'rgba(14,14,18,0.7)',
        padding: { left: 14, right: 14, top: 10, bottom: 10 },
        align: 'center'
      })
      .setScrollFactor(0)
      .setDepth(2000)
      .setOrigin(0.5)
      .setVisible(false);
    this.scale.on('resize', () => {
      this.repositionOverlay();
      this.updateCameraZoom();
    });

    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasdKeys = this.input.keyboard!.addKeys('W,A,S,D') as Record<
      'W' | 'A' | 'S' | 'D',
      Phaser.Input.Keyboard.Key
    >;
    // Track arrow keys directly via window events as a parallel signal to
    // Phaser's cursor state, which can spuriously reset on blur/pause.
    const onArrowDown = (e: KeyboardEvent): void => {
      if (e.code === 'ArrowUp') this.arrowState.up = true;
      else if (e.code === 'ArrowDown') this.arrowState.down = true;
      else if (e.code === 'ArrowLeft') this.arrowState.left = true;
      else if (e.code === 'ArrowRight') this.arrowState.right = true;
    };
    const onArrowUp = (e: KeyboardEvent): void => {
      if (e.code === 'ArrowUp') this.arrowState.up = false;
      else if (e.code === 'ArrowDown') this.arrowState.down = false;
      else if (e.code === 'ArrowLeft') this.arrowState.left = false;
      else if (e.code === 'ArrowRight') this.arrowState.right = false;
    };
    window.addEventListener('keydown', onArrowDown, true);
    window.addEventListener('keyup', onArrowUp, true);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('keydown', onArrowDown, true);
      window.removeEventListener('keyup', onArrowUp, true);
    });

    this.applyFlags(this.flags);
    onFlagsChange((next) => {
      this.flags = next;
      this.applyFlags(next);
    });

    this.time.addEvent({
      delay: SPAWN_INTERVAL_MS,
      loop: true,
      callback: () => {
        if (this.state === 'playing' && !this.tutorialActive) this.trySpawnWorkItem();
      }
    });

    onTutorialSkip(() => this.handleSkipTutorial());
    this.showPregameOverlay();
    this.updateTimerHud(LEVEL_DURATION_MS);
    this.updateHudCarry();
    this.updateHudCash();
  }

  update(time: number): void {
    if (!this.player.body) return;

    if (this.state !== 'playing') {
      this.player.setVelocity(0, 0);
      return;
    }

    if (!this.tutorialActive) {
      const remainingMs = Math.max(0, this.levelEndsAt - time);
      this.updateTimerHud(remainingMs);
      if (remainingMs <= 0) {
        this.endLevel();
        return;
      }
    }

    const speed = this.flags['ability.runFaster'] ? PLAYER_FAST_SPEED : PLAYER_BASE_SPEED;
    const touch = getTouch();
    const upDown = this.arrowState.up || this.cursors.up?.isDown || this.wasdKeys.W?.isDown || touch.up;
    const downDown =
      this.arrowState.down || this.cursors.down?.isDown || this.wasdKeys.S?.isDown || touch.down;
    const leftDown =
      this.arrowState.left || this.cursors.left?.isDown || this.wasdKeys.A?.isDown || touch.left;
    const rightDown =
      this.arrowState.right || this.cursors.right?.isDown || this.wasdKeys.D?.isDown || touch.right;

    let vx = 0;
    let vy = 0;
    if (upDown && !downDown) vy = -1;
    else if (downDown && !upDown) vy = 1;
    if (leftDown && !rightDown) vx = -1;
    else if (rightDown && !leftDown) vx = 1;
    if (vx !== 0 && vy !== 0) {
      const inv = 1 / Math.SQRT2;
      vx *= inv;
      vy *= inv;
    }
    this.player.setVelocity(vx * speed, vy * speed);
    if (vx < 0) this.player.flipX = true;
    else if (vx > 0) this.player.flipX = false;

    const isMoving = vx !== 0 || vy !== 0;
    if (isMoving && this.tutorialActive && this.tutorialStep === 'move') {
      this.advanceTutorial();
    }
    const walkKey = this.playerWalkKey();
    if (isMoving) {
      if (this.player.anims.currentAnim?.key !== walkKey || !this.player.anims.isPlaying) {
        this.player.anims.play(walkKey);
      }
    } else if (this.player.anims.isPlaying) {
      this.player.anims.stop();
      this.player.setTexture(this.playerIdleKey());
    }

    this.updateCarryPositions();
    this.tickDeadlines(time);
  }

  private startLevel(promote = false): void {
    // Resolve target level: progression result OR LD-flag override, whichever is higher.
    const flagLevel = this.flags['career.startLevel'];
    if (this.state === 'pregame') {
      this.currentLevel = flagLevel;
    } else if (this.state === 'gameover') {
      const base = promote ? (nextLevel(this.currentLevel) ?? this.currentLevel) : this.currentLevel;
      this.currentLevel = maxLevel(base, flagLevel);
    }

    this.state = 'playing';
    this.cash = 0;
    this.carrying.forEach((e) => e.sprite.destroy());
    this.carrying = [];
    this.workItems.getChildren().forEach((obj) => {
      const w = obj as WorkSprite;
      w.__bar?.destroy();
      w.__valueLabel?.destroy();
    });
    this.workItems.clear(true, true);
    this.fixItems.clear(true, true);
    this.officeDesk.cooldownUntil = 0;
    this.complianceDesk.cooldownUntil = 0;
    this.cashDesk.cooldownUntil = 0;
    this.player.setPosition(this.worldWidth / 2, this.worldHeight / 2);
    this.player.setVelocity(0, 0);
    this.applyPlayerAvatar();
    this.levelEndsAt = this.time.now + LEVEL_DURATION_MS;
    hideStartOverlay();
    this.updateHudCarry();
    this.updateHudCash();
    this.updateTimerHud(LEVEL_DURATION_MS);
    this.updateIdleMessage();
  }

  private endLevel(): void {
    this.state = 'gameover';
    this.player.setVelocity(0, 0);
    this.updateTimerHud(0);
    if (isUpdatePending()) {
      reloadForUpdate();
      return;
    }
    const meta = levelMeta[this.currentLevel];
    const fastTrack = this.flags['career.fastTrack'];
    const threshold = thresholdFor(this.currentLevel, fastTrack);
    const promotion = nextLevel(this.currentLevel);
    const earnedEnough = isFinite(threshold) && this.cash >= threshold;
    const promoted = earnedEnough && promotion !== null;
    onStartButton(() => this.startLevel(promoted));

    if (promoted && promotion) {
      const next = levelMeta[promotion];
      const nextThreshold = thresholdFor(promotion, fastTrack);
      const nextGoal = isFinite(nextThreshold)
        ? `Earn $${nextThreshold.toLocaleString()} in 60s for the next promotion.`
        : 'Top of the ladder — keep stacking cash.';
      showStartOverlay({
        role: next.title,
        roleDesc: next.subtitle,
        goal: nextGoal,
        buttonText: `Start ${next.title} Round`,
        avatarUrl: this.avatarUrlFor(promotion),
        banner: {
          text: `Promoted → ${next.title}  ·  Earned $${this.cash.toLocaleString()}`,
          tone: 'success'
        }
      });
    } else if (!promotion) {
      // Already at top of ladder.
      showStartOverlay({
        role: meta.title,
        roleDesc: meta.subtitle,
        goal: 'Top of the ladder — keep stacking cash.',
        buttonText: 'Start Another Round',
        avatarUrl: this.avatarUrlFor(this.currentLevel),
        banner: {
          text: `Partner Round Ended  ·  Earned $${this.cash.toLocaleString()}`,
          tone: 'info'
        }
      });
    } else {
      const shortBy = Math.max(0, threshold - this.cash);
      showStartOverlay({
        role: meta.title,
        roleDesc: meta.subtitle,
        goal: `Earn $${threshold.toLocaleString()} in 60s to get promoted.`,
        buttonText: `Try ${meta.title} Round Again`,
        avatarUrl: this.avatarUrlFor(this.currentLevel),
        banner: {
          text: `Time Up  ·  Earned $${this.cash.toLocaleString()}  ·  Short by $${shortBy.toLocaleString()}`,
          tone: 'failure'
        }
      });
    }
  }

  private showPregameOverlay(extraBanner?: { text: string; tone: 'success' | 'failure' | 'info' }): void {
    this.state = 'pregame';
    const startLevel = this.flags['career.startLevel'];
    const meta = levelMeta[startLevel];
    const fastTrack = this.flags['career.fastTrack'];
    const threshold = thresholdFor(startLevel, fastTrack);
    const goal =
      isFinite(threshold)
        ? `Earn $${threshold.toLocaleString()} in 60s to get promoted.`
        : 'Top of the ladder — keep stacking cash.';

    if (!tutorialCompleted()) {
      onStartButton(() => this.startTutorial());
      showStartOverlay({
        role: meta.title,
        roleDesc: meta.subtitle,
        goal: 'New here? Run the quick tutorial first — it takes about a minute.',
        buttonText: 'Start Tutorial',
        avatarUrl: this.avatarUrlFor(startLevel),
        banner: extraBanner,
        tutorial: { mode: 'skip', onClick: () => this.skipTutorialFromStart() }
      });
      return;
    }

    onStartButton(() => this.startLevel(false));
    showStartOverlay({
      role: meta.title,
      roleDesc: meta.subtitle,
      goal,
      buttonText: `Start ${meta.title} Round`,
      avatarUrl: this.avatarUrlFor(startLevel),
      banner: extraBanner,
      tutorial: { mode: 'replay', onClick: () => this.startTutorial() }
    });
  }

  private skipTutorialFromStart(): void {
    markTutorialCompleted();
    this.showPregameOverlay();
  }

  private startTutorial(): void {
    this.tutorialActive = true;
    this.state = 'playing';
    this.currentLevel = 'admin';
    this.cash = 0;
    this.carrying.forEach((e) => e.sprite.destroy());
    this.carrying = [];
    this.workItems.getChildren().forEach((obj) => {
      const w = obj as WorkSprite;
      w.__bar?.destroy();
      w.__valueLabel?.destroy();
    });
    this.workItems.clear(true, true);
    this.fixItems.clear(true, true);
    this.officeDesk.cooldownUntil = 0;
    this.complianceDesk.cooldownUntil = 0;
    this.cashDesk.cooldownUntil = 0;
    this.player.setPosition(this.worldWidth / 2, this.worldHeight / 2);
    this.player.setVelocity(0, 0);
    this.applyPlayerAvatar();
    this.levelEndsAt = Number.POSITIVE_INFINITY;
    hideStartOverlay();
    this.hideTimerUi();
    this.updateHudCarry();
    this.updateHudCash();
    this.updateIdleMessage();
    this.setTutorialStep('move');
  }

  private setTutorialStep(step: TutorialStep): void {
    this.tutorialStep = step;
    const info = TUTORIAL_STEPS[step];
    showTutorialBanner(`${info.index}/${info.total}`, info.text);
    this.unhighlightAllDesks();
    if (step === 'office') this.highlightDesk(this.officeDesk);
    else if (step === 'compliance') this.highlightDesk(this.complianceDesk);
    else if (step === 'cashout') this.highlightDesk(this.cashDesk);
  }

  private advanceTutorial(): void {
    if (!this.tutorialActive || !this.tutorialStep) return;
    const idx = TUTORIAL_ORDER.indexOf(this.tutorialStep);
    const next = TUTORIAL_ORDER[idx + 1];
    if (!next) {
      this.completeTutorial();
      return;
    }
    this.setTutorialStep(next);
    if (next === 'pickup') this.spawnTutorialWorkItem();
  }

  private spawnTutorialWorkItem(): void {
    const spec = workSpecsByType['fileDocs'];
    if (!spec) return;
    const x = Math.min(this.worldWidth - 80, this.player.x + 200);
    const y = this.player.y;
    this.spawnWorkItemAt(spec, x, y, 600_000);
  }

  private completeTutorial(): void {
    markTutorialCompleted();
    this.endTutorialRun({
      text: `Tutorial complete — you earned $${this.cash.toLocaleString()}!`,
      tone: 'success'
    });
  }

  private handleSkipTutorial(): void {
    if (!this.tutorialActive) return;
    markTutorialCompleted();
    this.endTutorialRun({ text: 'Tutorial skipped — you can replay it any time.', tone: 'info' });
  }

  private endTutorialRun(banner: { text: string; tone: 'success' | 'failure' | 'info' }): void {
    this.tutorialActive = false;
    this.tutorialStep = null;
    hideTutorialBanner();
    this.unhighlightAllDesks();
    this.showTimerUi();
    this.carrying.forEach((e) => e.sprite.destroy());
    this.carrying = [];
    this.workItems.getChildren().forEach((obj) => {
      const w = obj as WorkSprite;
      w.__bar?.destroy();
      w.__valueLabel?.destroy();
    });
    this.workItems.clear(true, true);
    this.fixItems.clear(true, true);
    this.cash = 0;
    this.updateHudCarry();
    this.updateHudCash();
    this.updateTimerHud(LEVEL_DURATION_MS);
    this.showPregameOverlay(banner);
  }

  private highlightDesk(desk: Desk): void {
    desk.glowTween?.stop();
    desk.glow.setVisible(true);
    desk.glow.setAlpha(1);
    desk.glowTween = this.tweens.add({
      targets: desk.glow,
      alpha: 0.25,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private unhighlightAllDesks(): void {
    for (const desk of [this.officeDesk, this.complianceDesk, this.cashDesk]) {
      desk.glowTween?.stop();
      desk.glowTween = undefined;
      desk.glow.setAlpha(1);
      desk.glow.setVisible(false);
    }
  }

  private hideTimerUi(): void {
    document.getElementById('hud-timer')?.setAttribute('hidden', '');
  }

  private showTimerUi(): void {
    document.getElementById('hud-timer')?.removeAttribute('hidden');
  }

  private repositionOverlay(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;
    this.idleMessage.setPosition(cx, cy);
  }

  private updateCameraZoom(): void {
    // Cover the viewport: zoom so the world is at least as large as the canvas
    // on both axes. No floor — small-screen worlds are aspect-matched to the
    // viewport, so cover-zoom resolves to roughly 1 and the whole world fits.
    const vw = this.scale.width;
    const vh = this.scale.height;
    const zoom = Math.max(vw / this.worldWidth, vh / this.worldHeight);
    this.cameras.main.setZoom(zoom);
  }

  private computeWorldSize(): { w: number; h: number } {
    const vw = this.scale.width;
    const vh = this.scale.height;
    if (Math.min(vw, vh) >= SMALL_SCREEN_THRESHOLD) {
      return { w: DESKTOP_WORLD_WIDTH, h: DESKTOP_WORLD_HEIGHT };
    }
    // Small screen: aspect-match the viewport so cover-zoom doesn't crop, and
    // floor each axis so the three desks (192x144) at corners always fit.
    const aspect = vw / vh;
    if (aspect >= 1) {
      const h = 600;
      const w = Math.max(900, Math.round(h * aspect));
      return { w, h };
    }
    const w = 600;
    const h = Math.max(900, Math.round(w / aspect));
    return { w, h };
  }

  private applyFlags(next: FlagSet): void {
    const prev = this.prevFlags;
    const themeChanged = !prev || next['theme.world'] !== prev['theme.world'];
    const seedChanged = !prev || next['world.seed'] !== prev['world.seed'];
    this.prevFlags = next;

    if (themeChanged) {
      const palette = themes[next['theme.world']];
      this.cameras.main.setBackgroundColor(palette.bg);
      this.groundLayer.fillColor = palette.ground;
    }
    if (themeChanged || seedChanged) this.regenerateDecor();

    this.cullIneligibleWorldItems();
    this.updateIdleMessage();
    this.updateHudCareer();
  }

  private regenerateDecor(): void {
    this.decorContainer.removeAll(true);
    this.groundPatchContainer.removeAll(true);

    const theme = themes[this.flags['theme.world']];
    const seed = this.flags['world.seed'];
    const exclusions = [this.officeDesk.bounds, this.complianceDesk.bounds, this.cashDesk.bounds];

    // Ground patches first (sit between groundLayer and decor)
    const patches = generateGroundPatches(
      this.worldWidth,
      this.worldHeight,
      seed,
      theme.groundPatches.map((p) => ({
        count: p.count,
        minSize: p.minSize,
        maxSize: p.maxSize
      }))
    );
    for (const p of patches) {
      const spec = theme.groundPatches[p.patchIndex];
      const obj =
        spec.shape === 'circle'
          ? this.add.circle(p.x, p.y, p.size / 2, spec.fill, spec.alpha)
          : this.add.rectangle(p.x, p.y, p.size, p.size * 0.7, spec.fill, spec.alpha);
      obj.setRotation((p.x * 0.013 + p.y * 0.017) % Math.PI);
      this.groundPatchContainer.add(obj);
    }

    const placements = generateDecorMulti(
      this.worldWidth,
      this.worldHeight,
      exclusions,
      seed,
      theme.decorVariants,
      theme.decorCount
    );
    // Sort by Y so closer-to-camera (lower-on-screen) decor renders on top.
    placements.sort((a, b) => a.y - b.y);
    for (const placement of placements) {
      const variant = theme.decorVariants[placement.variantIndex];
      const sprite = this.add.sprite(placement.x, placement.y, variant.key);
      sprite.setOrigin(0.5, 1);
      sprite.setScale(placement.scale);
      sprite.setDepth(-50 + placement.y * 0.001);
      this.decorContainer.add(sprite);
    }
  }

  private createDesk(
    key: DeskKey,
    x: number,
    y: number,
    style: { fill: number; edge: number; label: string }
  ): Desk {
    const cx = x + DESK_W / 2;
    const cy = y + DESK_H / 2;
    const rect = this.add
      .rectangle(cx, cy, DESK_W, DESK_H, style.fill)
      .setStrokeStyle(4, style.edge)
      .setDepth(-10);
    const label = this.add
      .text(cx, cy, style.label, {
        fontFamily: 'ui-monospace, Menlo, monospace',
        fontSize: '20px',
        color: '#ffffff',
        fontStyle: 'bold'
      })
      .setOrigin(0.5)
      .setDepth(-9);
    const zone = this.add.zone(cx, cy, DESK_W, DESK_H);
    this.physics.add.existing(zone, true);
    const bounds: ExcludeRect = { x: x - 16, y: y - 16, w: DESK_W + 32, h: DESK_H + 32 };
    const glow = this.add
      .rectangle(cx, cy, DESK_W + 28, DESK_H + 28, 0x000000, 0)
      .setStrokeStyle(4, 0xfbbf24)
      .setDepth(-8)
      .setVisible(false);
    return { key, rect, label, zone, cx, cy, topY: y, bounds, cooldownUntil: 0, glow };
  }

  private trySpawnWorkItem(): void {
    if (this.workItems.getLength() >= MAX_ACTIVE_ITEMS) return;
    const spec = pickRandomSpec(this.currentLevel);
    if (!spec) return;

    const rand = mulberry32(Math.floor(this.time.now * 0.97 + Math.random() * 1e6));
    const pos = pickRandomSpawn(
      this.worldWidth,
      this.worldHeight,
      this.officeDesk.bounds,
      rand,
      { x: this.player.x, y: this.player.y, minDist: 120 }
    );
    if (
      pointInRect(pos.x, pos.y, this.complianceDesk.bounds) ||
      pointInRect(pos.x, pos.y, this.cashDesk.bounds)
    ) return;

    this.spawnWorkItemAt(spec, pos.x, pos.y);
  }

  private spawnWorkItemAt(
    spec: WorkSpec,
    x: number,
    y: number,
    deadlineMsOverride?: number
  ): WorkSprite {
    const item = this.workItems.create(x, y, spec.iconKey) as WorkSprite;
    item.setDepth(5);
    item.body!.setSize(32, 32);
    item.__spec = spec;
    item.__expiresAt = this.time.now + (deadlineMsOverride ?? spec.deadlineMs);

    const bar = this.add
      .rectangle(x, y - 24, 30, 4, 0x4ade80)
      .setStrokeStyle(1, 0x000000, 0.5)
      .setOrigin(0.5)
      .setDepth(6);
    item.__bar = bar;

    const valueLabel = this.add
      .text(x, y + 22, `$${spec.value}`, {
        fontFamily: 'ui-monospace, Menlo, monospace',
        fontSize: '12px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3
      })
      .setOrigin(0.5)
      .setDepth(6);
    item.__valueLabel = valueLabel;

    this.tweens.add({
      targets: item,
      y: y - 4,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    return item;
  }

  private spawnFixItem(entryId: number): void {
    const rand = mulberry32(Math.floor(this.time.now * 1.13 + entryId));
    const pos = pickRandomSpawn(this.worldWidth, this.worldHeight, this.complianceDesk.bounds, rand, {
      x: this.player.x,
      y: this.player.y,
      minDist: 80
    });
    const fix = this.fixItems.create(pos.x, pos.y, STAGE_TEXTURES.fix) as FixSprite;
    fix.setDepth(5);
    fix.body!.setSize(28, 28);
    fix.__entryId = entryId;
    this.tweens.add({
      targets: fix,
      scale: 1.2,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private tickDeadlines(now: number): void {
    this.workItems.getChildren().forEach((obj) => {
      const item = obj as WorkSprite;
      const expiresAt = item.__expiresAt ?? 0;
      const total = item.__spec?.deadlineMs ?? 1;
      const remaining = Math.max(0, expiresAt - now);
      const ratio = remaining / total;

      if (item.__bar) {
        item.__bar.x = item.x;
        item.__bar.y = item.y - 24;
        item.__bar.width = Math.max(0, ratio * 30);
        item.__bar.fillColor = ratio > 0.5 ? 0x4ade80 : ratio > 0.25 ? 0xfacc15 : 0xef4444;
      }
      if (item.__valueLabel) {
        item.__valueLabel.x = item.x;
        item.__valueLabel.y = item.y + 22;
      }
      if (remaining <= 0) {
        const x = item.x;
        const y = item.y;
        item.__bar?.destroy();
        item.__valueLabel?.destroy();
        item.destroy();
        this.flashFloating(x, y - 10, 'expired', '#ef4444', 14);
      }
    });
  }

  private collectWorkItem(item: WorkSprite): void {
    if (!item.active || !item.__spec) return;
    if (!this.canPickUp()) return;

    const spec = item.__spec;
    item.__bar?.destroy();
    item.__valueLabel?.destroy();
    item.destroy();
    this.addEntry(spec, 'work');
    if (this.tutorialActive && this.tutorialStep === 'pickup') this.advanceTutorial();
  }

  private collectFixItem(fix: FixSprite): void {
    if (!fix.active) return;
    const entryId = fix.__entryId ?? -1;
    fix.destroy();
    const entry = this.carrying.find((e) => e.id === entryId);
    if (!entry || entry.stage !== 'rejected') return;
    entry.stage = 'approved';
    entry.sprite.setTexture(STAGE_TEXTURES.approved);
    this.flashFloating(this.player.x, this.player.y - 36, 'fixed', '#60a5fa', 16);
    this.updateHudCarry();
    if (this.tutorialActive && this.tutorialStep === 'wrench') this.advanceTutorial();
  }

  private addEntry(spec: WorkSpec, stage: EntryStage): void {
    const sprite = this.add.sprite(this.player.x, this.player.y, this.iconForStage(spec, stage));
    sprite.setScale(0.85);
    sprite.setDepth(this.player.depth + 1);
    this.carrying.push({
      id: this.nextEntryId++,
      workType: spec.type,
      stage,
      value: spec.value,
      sprite
    });
    this.updateHudCarry();
  }

  private iconForStage(spec: WorkSpec, stage: EntryStage): string {
    switch (stage) {
      case 'work':
        return spec.iconKey;
      case 'tbe':
        return STAGE_TEXTURES.tbe;
      case 'approved':
        return STAGE_TEXTURES.approved;
      case 'rejected':
        return STAGE_TEXTURES.rejected;
    }
  }

  private canPickUp(): boolean {
    const cap = this.flags['ability.carryMore'] ? CARRY_CAP_BIG : CARRY_CAP_BASE;
    return this.carrying.length < cap;
  }

  private updateCarryPositions(): void {
    for (let i = 0; i < this.carrying.length; i++) {
      const c = this.carrying[i];
      c.sprite.x = this.player.x;
      c.sprite.y = this.player.y - 26 - i * 7;
      c.sprite.setDepth(this.player.depth + 1 + i);
    }
  }

  private handleDesk(desk: Desk, action: () => boolean): void {
    if (this.state !== 'playing') return;
    if (this.time.now < desk.cooldownUntil) return;
    if (!action()) return;
    desk.cooldownUntil = this.time.now + DESK_COOLDOWN_MS;
  }

  private handleOffice(): void {
    this.handleDesk(this.officeDesk, () => {
      const works = this.carrying.filter((e) => e.stage === 'work');
      if (works.length === 0) return false;
      works.forEach((e) => {
        e.stage = 'tbe';
        e.sprite.setTexture(STAGE_TEXTURES.tbe);
      });
      this.flashAtDesk(this.officeDesk, `${works.length} → TBE`, '#fbbf24');
      this.updateHudCarry();
      if (this.tutorialActive && this.tutorialStep === 'office') this.advanceTutorial();
      return true;
    });
  }

  private handleCompliance(): void {
    this.handleDesk(this.complianceDesk, () => {
      const tbes = this.carrying.filter((e) => e.stage === 'tbe');
      if (tbes.length === 0) return false;
      let approved = 0;
      let rejected = 0;
      const forceReject = this.tutorialActive && this.tutorialStep === 'compliance';
      tbes.forEach((e) => {
        if (forceReject || Math.random() < REJECTION_RATE) {
          e.stage = 'rejected';
          e.sprite.setTexture(STAGE_TEXTURES.rejected);
          this.spawnFixItem(e.id);
          rejected++;
        } else {
          e.stage = 'approved';
          e.sprite.setTexture(STAGE_TEXTURES.approved);
          approved++;
        }
      });
      if (approved > 0) this.flashAtDesk(this.complianceDesk, `${approved} approved`, '#16a34a');
      if (rejected > 0)
        this.flashAtDesk(
          this.complianceDesk,
          `${rejected} rejected — find the wrench`,
          '#dc2626',
          approved > 0 ? 28 : 0
        );
      this.updateHudCarry();
      if (rejected > 0) this.pauseForComplianceAlert(rejected);
      if (this.tutorialActive && this.tutorialStep === 'compliance') this.advanceTutorial();
      return true;
    });
  }

  private pauseForComplianceAlert(rejectedCount: number): void {
    showCompliancePopup(rejectedCount, this.fixIconDataUrl);
    // Pausing the scene halts the clock, physics, timers, and animations,
    // so the round timer stops and items don't expire while the popup is up.
    this.scene.pause();
    // delayedCall is a Phaser timer, but timers are paused with the scene — use
    // a plain timeout so the popup actually closes itself while paused.
    window.setTimeout(() => {
      hideCompliancePopup();
      if (this.scene.isPaused()) this.scene.resume();
    }, COMPLIANCE_PAUSE_MS);
  }

  private extractTextureAsDataUrl(key: string): string {
    const tex = this.textures.get(key);
    const source = tex.getSourceImage();
    if (source instanceof HTMLCanvasElement) return source.toDataURL('image/png');
    return '';
  }

  private handleCash(): void {
    this.handleDesk(this.cashDesk, () => {
      const approved = this.carrying.filter((e) => e.stage === 'approved');
      if (approved.length === 0) return false;
      const base = approved.reduce((sum, e) => sum + e.value, 0);
      const multiplier = this.flags['office.expressLane'] ? 1.5 : 1;
      const earned = Math.round(base * multiplier);
      this.cash += earned;
      approved.forEach((e) => e.sprite.destroy());
      this.carrying = this.carrying.filter((e) => e.stage !== 'approved');
      this.flashAtDesk(this.cashDesk, `+$${earned}`, '#34d399');
      if (multiplier > 1) this.flashAtDesk(this.cashDesk, 'EXPRESS LANE 1.5×', '#fbbf24', 28);
      this.updateHudCarry();
      this.updateHudCash();
      if (this.tutorialActive && this.tutorialStep === 'cashout') this.advanceTutorial();
      return true;
    });
  }

  private flashAtDesk(desk: Desk, msg: string, color: string, yOffset = 0): void {
    this.flashFloating(desk.cx, desk.topY - 8 + yOffset, msg, color, 18);
  }

  private flashFloating(x: number, y: number, msg: string, color: string, fontSize = 16): void {
    const text = this.add
      .text(x, y, msg, {
        fontFamily: 'ui-monospace, Menlo, monospace',
        fontSize: `${fontSize}px`,
        color,
        fontStyle: 'bold'
      })
      .setOrigin(0.5)
      .setDepth(60);
    this.tweens.add({
      targets: text,
      y: y - 36,
      alpha: 0,
      duration: 1200,
      onComplete: () => text.destroy()
    });
  }

  private cullIneligibleWorldItems(): void {
    this.workItems.getChildren().forEach((obj) => {
      const item = obj as WorkSprite;
      if (!item.__spec) return;
      if (item.__spec.level !== this.currentLevel) {
        item.__bar?.destroy();
        item.__valueLabel?.destroy();
        item.destroy();
      }
    });
  }

  private updateIdleMessage(): void {
    const pool = specsForLevel(this.currentLevel);
    if (pool.length === 0 && this.state === 'playing') {
      this.idleMessage.setText('No work configured for this level.');
      this.idleMessage.setVisible(true);
      this.repositionOverlay();
    } else {
      this.idleMessage.setVisible(false);
    }
  }

  private updateHudCareer(): void {
    const titleEl = hudEl('hud-career');
    if (titleEl) titleEl.textContent = levelMeta[this.currentLevel].title;
    const goalEl = hudEl('hud-goal');
    if (goalEl) {
      const threshold = thresholdFor(this.currentLevel, this.flags['career.fastTrack']);
      goalEl.textContent = isFinite(threshold)
        ? `$${this.cash.toLocaleString()} / $${threshold.toLocaleString()}`
        : `$${this.cash.toLocaleString()} (top tier)`;
    }
  }

  private updateTimerHud(remainingMs: number): void {
    const el = hudEl('hud-time');
    if (!el) return;
    const total = Math.ceil(remainingMs / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    el.textContent = `${m}:${s.toString().padStart(2, '0')}`;
    el.classList.toggle('warning', remainingMs <= 10_000);
  }

  private updateHudCash(): void {
    const el = hudEl('hud-cash');
    if (el) el.textContent = `$${this.cash.toLocaleString()}`;
    this.updateHudCareer();
  }

  private updateHudCarry(): void {
    const el = hudEl('hud-carry');
    if (!el) return;
    if (this.carrying.length === 0) {
      el.textContent = 'empty';
      return;
    }
    const counts = { work: 0, tbe: 0, approved: 0, rejected: 0 };
    let value = 0;
    for (const e of this.carrying) {
      counts[e.stage]++;
      value += e.value;
    }
    const cap = this.flags['ability.carryMore'] ? CARRY_CAP_BIG : CARRY_CAP_BASE;
    const parts: string[] = [];
    if (counts.work) parts.push(`${counts.work}w`);
    if (counts.tbe) parts.push(`${counts.tbe}T`);
    if (counts.approved) parts.push(`${counts.approved}✓`);
    if (counts.rejected) parts.push(`${counts.rejected}✗`);
    el.textContent = `${this.carrying.length}/${cap} (${parts.join(' ')}) · $${value}`;
  }

  private playerIdleKey(): string {
    return `player-${this.currentLevel}-idle`;
  }

  private playerWalkKey(): string {
    return `player-${this.currentLevel}-walk`;
  }

  private applyPlayerAvatar(): void {
    this.player.anims.stop();
    this.player.setTexture(this.playerIdleKey());
  }

  private avatarUrlFor(level: CareerLevel): string {
    return this.extractTextureAsDataUrl(`player-${level}-idle`);
  }

  private makePlayerTextures(): void {
    for (const level of careerLevels) {
      this.drawPlayerFrame(level, 'idle');
      this.drawPlayerFrame(level, 'walk-a');
      this.drawPlayerFrame(level, 'walk-b');
    }
  }

  private drawPlayerFrame(level: CareerLevel, frame: 'idle' | 'walk-a' | 'walk-b'): void {
    const key = `player-${level}-${frame}`;
    if (this.textures.exists(key)) return;
    const w = 24;
    const h = 32;
    const tex = this.textures.createCanvas(key, w, h);
    if (!tex) return;
    const ctx = tex.getContext();
    ctx.clearRect(0, 0, w, h);
    const style = AVATAR_STYLES[level];

    const bob = frame === 'idle' ? 0 : -1;
    const leftLeg = frame === 'walk-a' ? -2 : 0;
    const rightLeg = frame === 'walk-b' ? -2 : 0;
    const leftArm = frame === 'walk-b' ? -1 : frame === 'walk-a' ? 1 : 0;
    const rightArm = frame === 'walk-a' ? -1 : frame === 'walk-b' ? 1 : 0;

    const fill = (color: number) => {
      ctx.fillStyle = '#' + color.toString(16).padStart(6, '0');
    };

    fill(style.pants);
    ctx.fillRect(5, 26 + leftLeg, 6, 6 - leftLeg);
    ctx.fillRect(13, 26 + rightLeg, 6, 6 - rightLeg);

    fill(style.shirt);
    ctx.fillRect(4, 12 + bob, 16, 14);
    ctx.fillRect(0, 14 + bob + leftArm, 4, 10);
    ctx.fillRect(20, 14 + bob + rightArm, 4, 10);

    if (style.lapel !== undefined) {
      fill(style.lapel);
      ctx.fillRect(5, 12 + bob, 2, 6);
      ctx.fillRect(17, 12 + bob, 2, 6);
    }

    if (style.vest !== undefined) {
      fill(style.vest);
      ctx.fillRect(8, 15 + bob, 8, 11);
    }

    if (style.collarV && style.shirtAlt !== undefined) {
      fill(style.shirtAlt);
      ctx.fillRect(10, 12 + bob, 4, 4);
    }

    if (level === 'admin' && style.shirtAlt !== undefined) {
      fill(style.shirtAlt);
      ctx.fillRect(11, 12 + bob, 2, 4);
      ctx.fillRect(8, 12 + bob, 2, 1);
      ctx.fillRect(14, 12 + bob, 2, 1);
    }

    if (level === 'paralegal' && style.shirtAlt !== undefined) {
      fill(style.shirtAlt);
      ctx.fillRect(8, 12 + bob, 2, 1);
      ctx.fillRect(14, 12 + bob, 2, 1);
    }

    if (style.tie !== undefined) {
      fill(style.tie);
      ctx.fillRect(11, 13 + bob, 2, 2);
      ctx.fillRect(11, 15 + bob, 2, 9);
    }

    if (style.tiePin !== undefined) {
      fill(style.tiePin);
      ctx.fillRect(11, 19 + bob, 2, 1);
    }

    if (style.pocketSquare !== undefined) {
      fill(style.pocketSquare);
      ctx.fillRect(6, 15 + bob, 2, 2);
    }

    if (style.badge !== undefined) {
      fill(style.badge);
      ctx.fillRect(6, 21 + bob, 3, 2);
    }

    fill(style.skin);
    ctx.beginPath();
    ctx.arc(12, 8 + bob, 6, 0, Math.PI * 2);
    ctx.fill();

    fill(style.hair);
    ctx.fillRect(6, 2 + bob, 12, 4);
    if (style.hairTuft === 'side') {
      ctx.fillRect(6, 6 + bob, 2, 2);
    } else if (style.hairTuft === 'long') {
      ctx.fillRect(6, 6 + bob, 2, 3);
      ctx.fillRect(16, 6 + bob, 2, 3);
    }

    fill(style.eyes);
    ctx.fillRect(9, 8 + bob, 2, 2);
    ctx.fillRect(13, 8 + bob, 2, 2);

    if (style.glasses !== undefined) {
      fill(style.glasses);
      ctx.fillRect(8, 7 + bob, 4, 1);
      ctx.fillRect(8, 10 + bob, 4, 1);
      ctx.fillRect(8, 8 + bob, 1, 2);
      ctx.fillRect(11, 8 + bob, 1, 2);
      ctx.fillRect(12, 7 + bob, 4, 1);
      ctx.fillRect(12, 10 + bob, 4, 1);
      ctx.fillRect(15, 8 + bob, 1, 2);
      ctx.fillRect(12, 8 + bob, 1, 2);
    }

    if (style.earpiece !== undefined) {
      fill(style.earpiece);
      ctx.fillRect(17, 7 + bob, 2, 3);
      if (style.earpieceWire !== undefined) {
        fill(style.earpieceWire);
        ctx.fillRect(18, 10 + bob, 1, 3);
      }
    }

    tex.refresh();
  }

  private createPlayerAnimations(): void {
    for (const level of careerLevels) {
      const key = `player-${level}-walk`;
      if (this.anims.exists(key)) continue;
      this.anims.create({
        key,
        frames: [
          { key: `player-${level}-walk-a` },
          { key: `player-${level}-idle` },
          { key: `player-${level}-walk-b` },
          { key: `player-${level}-idle` }
        ],
        frameRate: 8,
        repeat: -1
      });
    }
  }
}
